import React, { useMemo, useState } from "react";
import { Image, Pressable, StyleSheet, View } from "react-native";
import { useTheme } from "../hooks/use-theme";
import { PickerOptionsMode, useDocumentUpload } from "../hooks/documents";
import { Document, DocumentType } from "../__generated__/graphql";
import Loading from "./Loading";
import Icon from "./Icon";
import Text from "./Text";

const PICTURE_SIZE = 56;
const CIRCLE_SIZE = 24;

type SelectPictureProps = {
    label: string;
    value?: Pick<Document, "documentUrl">;
    onSelect: Parameters<typeof useDocumentUpload>[0]["onSelect"];
    withPrefix?: boolean;
    documentType: DocumentType;
    error?: boolean;
    webUploadFn?: string;
    modes?: PickerOptionsMode[];
    disabled?: boolean;
    enableCropZoom?: boolean;
};

const SelectPicture = ({
    label,
    value,
    withPrefix = false,
    onSelect,
    documentType,
    error,
    webUploadFn,
    modes = [PickerOptionsMode.Camera, PickerOptionsMode.Library],
    disabled = false,
    enableCropZoom = false,
}: SelectPictureProps) => {
    const styles = useStyles();
    const { colors } = useTheme();

    const [isLoading, setIsLoading] = useState(false);

    const { documentDropdownProps } = useDocumentUpload({
        documentType,
        generateUniqueId: withPrefix,
        isPublic: true,
        setIsLoading,
        onSelect,
        webUploadFn,
        modes,
        enableCropZoom,
    });

    return (
        <Pressable
            style={[styles.container, isLoading && { opacity: 0.5 }]}
            {...documentDropdownProps}
            disabled={isLoading || disabled}
        >
            {value ? (
                <Image source={{ uri: value.documentUrl }} style={styles.picture} />
            ) : (
                <View style={[styles.picture, error && { backgroundColor: colors.error }]}>
                    {!isLoading ? (
                        <View
                            style={[
                                styles.iconCircle,
                                { backgroundColor: !error ? colors.secondary : "white" },
                            ]}
                        >
                            <Icon
                                name="cloud-upload"
                                size={CIRCLE_SIZE - 10}
                                color={!error ? "white" : colors.error}
                            />
                        </View>
                    ) : (
                        <Loading />
                    )}
                </View>
            )}
            <Text style={[styles.labelText, error && { color: colors.error }]} numberOfLines={1}>
                {label}
            </Text>
        </Pressable>
    );
};

const useStyles = () => {
    const { colors, fonts, gridSize } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                },
                picture: {
                    height: PICTURE_SIZE,
                    width: PICTURE_SIZE,
                    borderRadius: PICTURE_SIZE / 2,
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: colors.accentLighter,
                },
                iconCircle: {
                    width: CIRCLE_SIZE,
                    height: CIRCLE_SIZE,
                    borderRadius: CIRCLE_SIZE / 2,
                    alignItems: "center",
                    justifyContent: "center",
                },
                labelText: {
                    ...fonts.bodyMedium,
                    color: colors.secondary,
                    marginLeft: gridSize,
                    flexShrink: 1,
                },
            }),
        [colors.accentLighter, colors.secondary, fonts.bodyMedium, gridSize],
    );
};

export default SelectPicture;
